import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../../utils/api';
import { toast } from 'sonner';
import {
  Plus, Search, Star, StarOff, Eye, Pencil, Trash2,
  FileText, Filter, ExternalLink, Copy,
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API_URL = getApiUrl();

const STATUS_COLORS = {
  published: 'bg-green-100 text-green-700',
  draft: 'bg-yellow-100 text-yellow-700',
  archived: 'bg-slate-100 text-slate-500',
};

const CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: 'trimester_1', label: 'Trimester 1' },
  { value: 'trimester_2', label: 'Trimester 2' },
  { value: 'trimester_3', label: 'Trimester 3' },
  { value: 'birth_preparation', label: 'Birth Preparation' },
  { value: 'garbh_sanskar', label: 'Garbh Sanskar' },
  { value: 'breathing_meditation', label: 'Breathing & Meditation' },
  { value: 'postpartum', label: 'Postpartum Recovery' },
  { value: 'expert_insights', label: 'Expert Insights' },
];

export default function BlogPostsPage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (categoryFilter) params.set('category', categoryFilter);
      const { data } = await axios.get(`${API_URL}/api/admin/blog/posts?${params}`);
      setPosts(data.posts || []);
    } catch (err) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const toggleFeatured = async (slug) => {
    try {
      await axios.patch(`${API_URL}/api/admin/blog/posts/${slug}/feature`);
      toast.success('Featured post updated');
      fetchPosts();
    } catch (err) {
      toast.error('Failed to update featured post');
    }
  };

  const deletePost = async (slug, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/blog/posts/${slug}`);
      toast.success('Post deleted');
      fetchPosts();
    } catch (err) {
      toast.error('Failed to delete post');
    }
  };

  const copyBlogUrl = (slug) => {
    const publicUrl = `https://www.soulsanskar.com/blogs/${slug}`;
    navigator.clipboard.writeText(publicUrl).then(() => {
      toast.success('Blog URL copied!');
    }).catch(() => {
      toast.error('Failed to copy URL');
    });
  };

  const openBlogUrl = (slug) => {
    window.open(`https://www.soulsanskar.com/blogs/${slug}`, '_blank');
  };

  const filteredPosts = posts.filter(p =>
    !searchQuery || p.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getCategoryLabel = (val) => CATEGORIES.find(c => c.value === val)?.label || val;

  return (
    <div className="space-y-6" data-testid="blog-posts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="blog-posts-title">Blog Posts</h1>
          <p className="text-sm text-slate-500 mt-1">{posts.length} total posts</p>
        </div>
        <Button
          onClick={() => navigate('/blog/posts/new')}
          className="gap-2"
          data-testid="create-post-btn"
        >
          <Plus className="w-4 h-4" /> New Post
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3" data-testid="blog-posts-filters">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search posts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="blog-search-input"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
          data-testid="blog-status-filter"
        >
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
          data-testid="blog-category-filter"
        >
          {CATEGORIES.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-20 text-slate-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">No posts found</p>
            <p className="text-sm mt-1 mb-4">Create your first blog post or seed sample data to get started.</p>
            <div className="flex items-center justify-center gap-3">
              <Button onClick={() => navigate('/blog/posts/new')} data-testid="empty-create-post-btn">
                <Plus className="w-4 h-4 mr-2" /> New Post
              </Button>
              <Button variant="outline" onClick={async () => {
                try {
                  await axios.post(`${API_URL}/api/admin/blog/seed`);
                  toast.success('Sample blog data seeded');
                  fetchPosts();
                } catch (err) { toast.error('Seed failed'); }
              }} data-testid="seed-posts-btn">
                Seed Sample Data
              </Button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="blog-posts-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Title</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600 hidden md:table-cell">Category</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">Author</th>
                  <th className="text-center px-4 py-3 font-medium text-slate-600">Status</th>
                  <th className="text-center px-4 py-3 font-medium text-slate-600 w-12">
                    <Star className="w-4 h-4 mx-auto text-amber-500" />
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">Date</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600 hidden md:table-cell">Readers</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600 w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPosts.map((post) => (
                  <tr
                    key={post.slug}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                    data-testid={`blog-post-row-${post.slug}`}
                  >
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/blog/posts/${post.slug}`)}
                        className="text-left font-medium text-slate-900 hover:text-indigo-600 transition-colors line-clamp-1"
                        data-testid={`blog-post-title-${post.slug}`}
                      >
                        {post.title}
                      </button>
                      {post.badge && (
                        <Badge variant="outline" className="ml-2 text-xs bg-amber-50 text-amber-700 border-amber-200">
                          {post.badge}
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 hidden md:table-cell">
                      {getCategoryLabel(post.category)}
                    </td>
                    <td className="px-4 py-3 text-slate-600 hidden lg:table-cell">
                      {post.author_name || '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[post.status] || STATUS_COLORS.draft}`}>
                        {post.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleFeatured(post.slug); }}
                        className="p-1 hover:bg-amber-50 rounded transition-colors"
                        title={post.is_featured ? 'Featured' : 'Set as featured'}
                        data-testid={`toggle-featured-${post.slug}`}
                      >
                        {post.is_featured
                          ? <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                          : <StarOff className="w-4 h-4 text-slate-300" />
                        }
                      </button>
                    </td>
                    <td className="px-4 py-3 text-slate-500 hidden lg:table-cell">
                      {formatDate(post.publish_date)}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600 hidden md:table-cell">
                      {(post.readers_count || 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => copyBlogUrl(post.slug)}
                          className="p-1.5 hover:bg-slate-100 rounded transition-colors"
                          title="Copy public URL"
                          data-testid={`copy-url-${post.slug}`}
                        >
                          <Copy className="w-4 h-4 text-slate-500" />
                        </button>
                        <button
                          onClick={() => openBlogUrl(post.slug)}
                          className="p-1.5 hover:bg-slate-100 rounded transition-colors"
                          title="Open in new tab"
                          data-testid={`open-post-${post.slug}`}
                        >
                          <ExternalLink className="w-4 h-4 text-slate-500" />
                        </button>
                        <button
                          onClick={() => navigate(`/blog/posts/${post.slug}`)}
                          className="p-1.5 hover:bg-slate-100 rounded transition-colors"
                          title="Edit"
                          data-testid={`edit-post-${post.slug}`}
                        >
                          <Pencil className="w-4 h-4 text-slate-500" />
                        </button>
                        <button
                          onClick={() => deletePost(post.slug, post.title)}
                          className="p-1.5 hover:bg-red-50 rounded transition-colors"
                          title="Delete"
                          data-testid={`delete-post-${post.slug}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
